import React from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import { MessageDescriptor, useIntl } from "react-intl";
import { msgs } from "../msgs";
import { FieldFormatter, ReadablePromotionDetail, WidgetEntity } from "../types";
import { ApprovalCard, CardHeader, Section, StatePill } from "./WidgetApproval";
import { formatWeekDaysField } from "./weekday-format";
import { PromotionDetailView } from "./widget-approval/promotion-detail";

/**
 * True when `content` is the ENRICHED promotion detail shape (`getPromotion`/
 * `getPromotionByTicker` — see `ReadablePromotionDetail`), not one of the OTHER, much thinner
 * shapes that also flow through the "object" widget for `entity: "promotion"` (e.g. a mutation
 * tool's post-execution confirmation, `{id, ticker}`-shaped). `translations` is a reliable
 * discriminator: it only ever exists on the full read DTO.
 */
function isReadablePromotionDetail(
  entity: WidgetEntity | undefined,
  obj: Record<string, unknown>
): obj is ReadablePromotionDetail & Record<string, unknown> {
  return (
    entity === "promotion" && Array.isArray(obj.translations) && typeof obj.rateType === "string"
  );
}

function formatRateField(rate: unknown): string {
  if (rate === null || rate === undefined) return "—";
  if (typeof rate === "string") return rate || "—";
  const r = rate as { rateType?: string; rateValue?: { value?: unknown; valueType?: string } };
  if (!r.rateValue) return "—";
  if (r.rateType === "PERCENTAGE") return `${r.rateValue.value ?? 0}%`;
  if (r.rateType === "CONSTANT") {
    if (r.rateValue.valueType === "MONEY") {
      const money = r.rateValue.value as { amount?: number; currency?: string } | null;
      if (money?.amount != null) return `${money.amount} ${money.currency ?? ""}`.trim();
    }
    return String(r.rateValue.value ?? "—");
  }
  return String(r.rateValue.value ?? "—");
}

const OBJECT_FIELD_CONFIG: Array<{
  key: string;
  labelMsg: MessageDescriptor;
  format?: FieldFormatter;
}> = [
  { key: "ticker", labelMsg: msgs.fieldTicker },
  { key: "propertyTicker", labelMsg: msgs.fieldPropertyTicker },
  { key: "parentPropertyTicker", labelMsg: msgs.fieldParentPropertyTicker },
  { key: "bookType", labelMsg: msgs.fieldBookType },
  { key: "isActive", labelMsg: msgs.fieldIsActive },
  { key: "isVisible", labelMsg: msgs.fieldIsVisible },
  { key: "rate", labelMsg: msgs.fieldRate, format: v => formatRateField(v) },
  { key: "group", labelMsg: msgs.fieldGroup, format: v => String(v ?? "").replace(/^GROUP_/, "") },
  { key: "isCombinable", labelMsg: msgs.fieldIsCombinable },
  { key: "sharingType", labelMsg: msgs.fieldSharingType },
];

// Booking/travel windows are rendered as a SINGLE compact row each, e.g.
// "2025-07-05 → 2025-07-10 · Lunes, Miércoles, Domingo". Built from the three
// related keys; returns null when the window has nothing to show.
const WINDOW_ROW_CONFIG: Array<{
  key: string;
  labelMsg: MessageDescriptor;
  startKey: string;
  endKey: string;
  weekDaysKey: string;
}> = [
  {
    key: "bookingWindow",
    labelMsg: msgs.fieldBookingWindow,
    startKey: "bookingWindowStart",
    endKey: "bookingWindowEnd",
    weekDaysKey: "bookingWindowWeekDays",
  },
  {
    key: "travelWindow",
    labelMsg: msgs.fieldTravelWindow,
    startKey: "travelWindowStart",
    endKey: "travelWindowEnd",
    weekDaysKey: "travelWindowWeekDays",
  },
  {
    key: "closeBookingWindow",
    labelMsg: msgs.fieldCloseBookingWindow,
    startKey: "closeBookingWindowStart",
    endKey: "closeBookingWindowEnd",
    weekDaysKey: "closeBookingWindowWeekDays",
  },
  {
    key: "closeTravelWindow",
    labelMsg: msgs.fieldCloseTravelWindow,
    startKey: "closeTravelWindowStart",
    endKey: "closeTravelWindowEnd",
    weekDaysKey: "closeTravelWindowWeekDays",
  },
];

function formatWindowRow(
  key: string,
  obj: Record<string, unknown>,
  startKey: string,
  endKey: string,
  weekDaysKey: string,
  locale: string
): string | null {
  const start = obj[startKey];
  const end = obj[endKey];
  const weekDays = obj[weekDaysKey];
  const hasStart = typeof start === "string" && start.length > 0;
  const hasEnd = typeof end === "string" && end.length > 0;
  const hasDays = Array.isArray(weekDays) && weekDays.length > 0;
  if (!hasStart && !hasEnd && !hasDays) return null;

  let datePart = "";
  if (hasStart && hasEnd) {
    if (key === "bookingWindow")
      datePart = `${moment(start).format("YYYY-MM-DD HH:mm:ss")} → ${moment(end).format(
        "YYYY-MM-DD HH:mm:ss"
      )}`;
    else datePart = `${moment(start).format("YYYY-MM-DD")} → ${moment(end).format("YYYY-MM-DD")}`;
  } else if (hasStart) datePart = moment(start).format("YYYY-MM-DD");
  else if (hasEnd) datePart = moment(end).format("YYYY-MM-DD");

  const daysPart = hasDays ? formatWeekDaysField(weekDays, locale) : "";
  if (datePart && daysPart) return `${datePart} · ${daysPart}`;
  return datePart || daysPart;
}

export interface WidgetObjectProps {
  content: unknown;
  propertyTicker?: string;
  entity?: WidgetEntity;
}

export function WidgetObject({
  content,
  propertyTicker,
  entity,
}: WidgetObjectProps): React.JSX.Element | null {
  const intl = useIntl();
  const yes = intl.formatMessage(msgs.confirmYes);
  const no = intl.formatMessage(msgs.confirmNo);

  const obj = (content ?? {}) as Record<string, unknown>;

  // Full promotion detail (getPromotion/getPromotionByTicker) gets its own rich renderer, reusing
  // the same building blocks as the create/update approval cards for coherence (translations,
  // inventory scope descriptor, geolocation, restrictions, windows, featured/give-night badges) —
  // see PromotionDetailView's own doc comment. Every OTHER "object" widget shape (mutation-result
  // confirmations, other entities) keeps the generic flat-field renderer below, unchanged.
  if (isReadablePromotionDetail(entity, obj)) {
    return <PromotionDetailView content={obj} propertyTicker={propertyTicker} />;
  }

  // Prefer the object's own propertyTicker (child engine) over the current engine ticker
  // passed as a prop, so links from an aggregator navigate to the correct child engine.
  const linkTicker =
    (typeof obj.propertyTicker === "string" && obj.propertyTicker) || propertyTicker;

  // The promotion id is no longer shown as its own row, but it's still needed to build the
  // ticker link's path.
  const promotionId = typeof obj.id === "string" ? obj.id : undefined;

  const items = OBJECT_FIELD_CONFIG.filter(({ key }) => key in obj)
    .map(({ key, labelMsg, format }) => {
      const v = obj[key];

      // Sharing type: only SHARED is shown (localized). SIMPLE (or anything else) is
      // hidden entirely, so we drop the row.
      if (key === "sharingType") {
        if (v !== "SHARED") return null;
        return {
          key,
          label: intl.formatMessage(labelMsg),
          display: intl.formatMessage(msgs.fieldSharingTypeShared),
        };
      }

      let display: string;
      if (format) {
        display = format(v, { yes, no, locale: intl.locale });
      } else if (typeof v === "boolean") {
        display = v ? yes : no;
      } else if (v === null || v === undefined) {
        display = "—";
      } else if (typeof v === "object") {
        display = JSON.stringify(v);
      } else {
        display = String(v);
      }
      return { key, label: intl.formatMessage(labelMsg), display };
    })
    .filter((row): row is { key: string; label: string; display: string } => row !== null);

  const windowItems = WINDOW_ROW_CONFIG.map(({ key, labelMsg, startKey, endKey, weekDaysKey }) => {
    const display = formatWindowRow(key, obj, startKey, endKey, weekDaysKey, intl.locale);
    return display == null ? null : { key, label: intl.formatMessage(labelMsg), display };
  }).filter((row): row is { key: string; label: string; display: string } => row !== null);

  // The ticker becomes the card header (as a link, when we can resolve one) and
  // isActive/isVisible become the same status pills as the create-approval card's
  // "Estado" section — the rest render as one bordered row per field, same as the
  // approval cards' ChangeRow/Section rows, instead of the previous plain bullet list.
  const tickerItem = items.find(({ key }) => key === "ticker");
  const isActiveRaw = obj.isActive;
  const isVisibleRaw = obj.isVisible;
  const hasStatus = typeof isActiveRaw === "boolean" || typeof isVisibleRaw === "boolean";
  const fieldItems = items.filter(({ key }) => !["ticker", "isActive", "isVisible"].includes(key));
  const allItems = [...fieldItems, ...windowItems];

  if (!tickerItem && !hasStatus && allItems.length === 0) return null;

  const headerTitle = tickerItem && (
    <>
      {linkTicker && promotionId ? (
        <Link
          to={`/${linkTicker}/promotions/${promotionId}`}
          className="inline-flex items-center gap-1 text-[#212121] hover:underline">
          {tickerItem.display}
          <em className="fa fa-external-link text-[10px]" />
        </Link>
      ) : (
        tickerItem.display
      )}
    </>
  );

  return (
    <div className="space-y-2">
      {(tickerItem || hasStatus || allItems.length > 0) && (
        <ApprovalCard>
          {tickerItem && <CardHeader title={headerTitle} />}
          {hasStatus && (
            <Section label={intl.formatMessage(msgs.approvalStateChange)}>
              <div className="flex flex-wrap gap-2">
                {typeof isActiveRaw === "boolean" && (
                  <StatePill icon={isActiveRaw ? "fa-check-circle" : "fa-minus-circle"}>
                    {intl.formatMessage(
                      isActiveRaw ? msgs.approvalStateActive : msgs.approvalStateInactive
                    )}
                  </StatePill>
                )}
                {typeof isVisibleRaw === "boolean" && (
                  <StatePill icon={isVisibleRaw ? "fa-eye" : "fa-eye-slash"}>
                    {intl.formatMessage(
                      isVisibleRaw ? msgs.approvalStateVisible : msgs.approvalStateNotVisible
                    )}
                  </StatePill>
                )}
              </div>
            </Section>
          )}
          {fieldItems.map(({ key, label, display }) => (
            <Section key={key} label={label}>
              <span className="text-[#212121] text-sm">{display}</span>
            </Section>
          ))}
          {windowItems.map(({ key, label, display }) => (
            <Section key={key} label={label}>
              <span className="text-[#212121] text-sm">{display}</span>
            </Section>
          ))}
        </ApprovalCard>
      )}
    </div>
  );
}
